import { ChangeDetectionStrategy, Component, inject, Input, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@app/shared/shared-components.module';
import {
  DISCRIMINATOR,
  getDistinctUserFriendlyDiscriminators,
  UserFriendlyDiscriminator
} from '@app/shared/models/selectable.interface';
import { NavigationService } from '@services/navigation/navigation.service';
import { MAP_NODES_ROUTE } from '@common/route-constants';

const USER_INPUT_DELAY = 1000;

@Component({
  selector: 'app-nodes-filter',
  imports: [
    MatButtonModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    SharedComponentsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './nodes-filter.component.html',
  styleUrl: './nodes-filter.component.scss'
})
export class NodesFilterComponent {
  // Inputs
  filterForm = input.required<FormGroup>();
  sortByFields = input<Record<string, string>>({});
  showFields = input<string[]>(['id', 'address', 'nodeType', 'copyToClipboard']);
  isLoading = input<boolean>(false);
  roomNames = input<string[]>([]);

  // Outputs
  filterChange = output<NodeFilter>();
  copyNodesToClipboard = output<void>();
  refreshNodeListEmitter = output<NodeFilter>();

  // Injected services
  private readonly navService = inject(NavigationService);

  // Internal state
  protected readonly Object = Object;
  protected nodeTypes = getDistinctUserFriendlyDiscriminators();
  protected typingTimer: ReturnType<typeof setTimeout> | undefined;
  protected filter: Record<string, string | string[]> = {};

  private buildNodeFilter(): NodeFilter {
    return {
      filterCriteria: this.filter,
      sortCriteria: {
        field: this.filterForm().value.sortBy,
        asc: this.filterForm().value.sortAsc
      }
    };
  }

  toggleSortOrder(): void {
    const asc = !this.filterForm().value.sortAsc;
    this.filterForm().get('sortAsc')?.setValue(asc);
    this.filterChange.emit(this.buildNodeFilter());
  }

  copyToClipboard(): void {
    this.copyNodesToClipboard.emit();
  }

  refreshNodeList(event: Event): void {
    // To avoid calling BE api on every enter keypress
    const isMappingPage = this.navService.getActiveSection().info.Id === MAP_NODES_ROUTE.id;
    if (isMappingPage && event instanceof PointerEvent && event.pointerType === '') {
      return;
    }
    this.refreshNodeListEmitter.emit(this.buildNodeFilter());
  }

  changeId(event: KeyboardEvent): void {
    this.filter.id = (event.target as HTMLInputElement).value;
    this.filterChange.emit(this.buildNodeFilter());
  }

  changeAddress(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    const isMappingPage = this.navService.getActiveSection().info.Id === MAP_NODES_ROUTE.id;

    if (!isMappingPage) {
      this.filterByAddress(value);
      return;
    }

    clearTimeout(this.typingTimer);
    if (event.key === 'Enter') {
      return;
    }

    this.typingTimer = setTimeout(() => {
      this.filterByAddress(value);
    }, USER_INPUT_DELAY);
  }

  filterByAddress(address: string): void {
    this.filter.address = address;
    this.filterChange.emit(this.buildNodeFilter());
  }

  changeNodeType(type: string): void {
    if (type === 'MW') {
      this.filter.nodeType = [DISCRIMINATOR.HCD405, DISCRIMINATOR.HCD038];
    } else {
      const discriminatorKey = Object.keys(UserFriendlyDiscriminator).find(
        (key) => UserFriendlyDiscriminator[key] === type
      );
      this.filter.nodeType = discriminatorKey ? DISCRIMINATOR[discriminatorKey] : '';
    }
    this.filterChange.emit(this.buildNodeFilter());
  }

  changeRoomName(roomName: string): void {
    this.filter.roomName = roomName || '';
    const nodeFilter = {
      filterCriteria: this.filter,
      sortCriteria: {
        field: this.filterForm().value.sortBy,
        asc: this.filterForm().value.sortAsc
      }
    };
    this.filterChange.emit(nodeFilter);
  }

  changeSortBy(): void {
    this.filterChange.emit(this.buildNodeFilter());
  }
}

export interface SortCriteria {
  field: string;
  asc: boolean;
}

export interface NodeFilter {
  filterCriteria: Record<string, string | string[]>;
  sortCriteria: SortCriteria;
}
